/**
 * Admin API helpers — for the /admin/suite/* endpoints.
 * Only callable by super-admins (ADMIN_USER_IDS).
 */
import { ApiError } from "./api";

type GetToken = () => Promise<string | null>;

async function authedFetch(
  getToken: GetToken,
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getToken();
  if (!token) throw new ApiError(401, "AUTH_REQUIRED", "Not signed in");
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(input, { ...init, headers });
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const code =
      (body && typeof body === "object" && typeof body.code === "string"
        ? body.code
        : null) ?? `HTTP_${res.status}`;
    const message =
      (body && typeof body === "object" && typeof body.error === "string"
        ? body.error
        : null) ?? res.statusText ?? "Request failed";
    throw new ApiError(res.status, code, message);
  }
  return (body ?? {}) as T;
}

function safeJson(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// ── Types ──────────────────────────────────────────────────────────────────

export interface AdminUser {
  clerkUserId: string;
  displayName: string | null;
  email: string | null;
  isSuiteAdmin: boolean;
  isSuperAdmin: boolean;
  orgId: string | null;
  orgName: string | null;
}

export interface AdminOrg {
  id: string;
  name: string;
  slug: string | null;
}

export interface OrgGrant {
  id: string | null;
  orgId: string;
  targetType: "user" | "group" | "all";
  targetId: string | null;
  productId: string | null;
  role: string;
  grantedBy: string;
  grantedAt: string;
}

export interface OrgMember {
  clerkUserId: string;
  displayName: string | null;
  email: string | null;
  role: string;
  isSuiteAdmin: boolean;
  isSuperAdmin: boolean;
}

export interface UserGroupAssignment {
  clerkUserId: string;
  groupId: string;
  groupName: string;
  orgId: string;
}

export interface CrossOrgRule {
  id: string;
  fromOrgId: string;
  fromOrgName: string | null;
  toOrgId: string | null;
  toOrgName: string | null;
  status: "active" | "pending" | "rejected";
  allowedUserIds: string[] | null;
  allowedGroupIds: string[] | null;
  adminMapsTo: string;
  managerMapsTo: string;
  memberMapsTo: string;
  productId: string | null;
  requestedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── API calls ──────────────────────────────────────────────────────────────

export async function adminIsSuperAdmin(getToken: GetToken): Promise<boolean> {
  const res = await authedFetch(getToken, "/admin/suite/is-super-admin");
  const body = await parseOrThrow<{ isSuperAdmin: boolean }>(res);
  return body.isSuperAdmin;
}

export async function adminListUsers(getToken: GetToken): Promise<AdminUser[]> {
  const res = await authedFetch(getToken, "/admin/suite/users");
  return parseOrThrow<AdminUser[]>(res);
}

export async function adminPromoteUser(getToken: GetToken, userId: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/users/${encodeURIComponent(userId)}/promote`, {
    method: "POST",
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminDemoteUser(getToken: GetToken, userId: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/users/${encodeURIComponent(userId)}/demote`, {
    method: "DELETE",
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminListOrgs(getToken: GetToken): Promise<AdminOrg[]> {
  const res = await authedFetch(getToken, "/admin/suite/orgs");
  return parseOrThrow<AdminOrg[]>(res);
}

export async function adminListOrgMembers(getToken: GetToken, orgId: string): Promise<OrgMember[]> {
  const res = await authedFetch(getToken, `/admin/suite/orgs/${encodeURIComponent(orgId)}/members`);
  return parseOrThrow<OrgMember[]>(res);
}

export async function adminListOrgGrants(
  getToken: GetToken,
  opts?: { productId?: string }
): Promise<OrgGrant[]> {
  const qs = opts?.productId ? `?productId=${encodeURIComponent(opts.productId)}` : "";
  const res = await authedFetch(getToken, `/admin/suite/org-grants${qs}`);
  return parseOrThrow<OrgGrant[]>(res);
}

export async function adminRevokeOrgGrant(
  getToken: GetToken,
  args: { orgId: string; targetType: "user" | "group" | "all"; targetId: string | null; productId: string | null }
): Promise<void> {
  const res = await authedFetch(getToken, "/admin/suite/org-grants/revoke", {
    method: "POST",
    body: JSON.stringify({
      orgId: args.orgId,
      targetType: args.targetType,
      targetId: args.targetId,
      productId: args.productId,
    }),
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminCreateOrg(
  getToken: GetToken,
  args: { name: string; slug?: string }
): Promise<AdminOrg> {
  const res = await authedFetch(getToken, "/admin/suite/orgs", {
    method: "POST",
    body: JSON.stringify(args),
  });
  return parseOrThrow<AdminOrg>(res);
}

export async function adminGrantOrgAccess(
  getToken: GetToken,
  args: { orgId: string; targetType: "all" | "user" | "group"; targetId: string | null; productId: string | null; role: string }
): Promise<void> {
  const res = await authedFetch(getToken, "/admin/suite/org-grants/grant", {
    method: "POST",
    body: JSON.stringify(args),
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminCreateCrossOrgRule(
  getToken: GetToken,
  args: { fromOrgId: string; toOrgId: string | null; productId: string | null; adminMapsTo: string; managerMapsTo: string; memberMapsTo: string; allowedUserIds?: string[] | null; allowedGroupIds?: string[] | null }
): Promise<CrossOrgRule> {
  const res = await authedFetch(getToken, "/admin/suite/cross-org", {
    method: "POST",
    body: JSON.stringify({ ...args, status: "active" }),
  });
  return parseOrThrow<CrossOrgRule>(res);
}

export async function adminListCrossOrgRules(getToken: GetToken): Promise<CrossOrgRule[]> {
  const res = await authedFetch(getToken, "/admin/suite/cross-org");
  return parseOrThrow<CrossOrgRule[]>(res);
}

export async function adminDeleteCrossOrgRule(getToken: GetToken, id: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/cross-org/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminApproveCrossOrgRule(getToken: GetToken, id: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/cross-org/${encodeURIComponent(id)}/approve`, {
    method: "POST",
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminRejectCrossOrgRule(getToken: GetToken, id: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/cross-org/${encodeURIComponent(id)}/reject`, {
    method: "POST",
  });
  await parseOrThrow<{ ok: true }>(res);
}

// ── Groups ──────────────────────────────────────────────────────────────────

export interface SuiteGroup {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  createdAt: string;
  createdBy: string;
}

export interface GroupMember {
  clerkUserId: string;
  groupId: string;
  assignedBy: string;
  assignedAt: string;
  displayName: string | null;
  email: string | null;
}

export interface GroupToolRole {
  groupId: string;
  productId: string;
  role: string;
}

export interface AdminProduct {
  id: string;
  name: string;
  description: string | null;
}

export async function adminListProducts(getToken: GetToken): Promise<AdminProduct[]> {
  const res = await authedFetch(getToken, "/admin/suite/products");
  return parseOrThrow<AdminProduct[]>(res);
}

export async function adminCreateProduct(
  getToken: GetToken,
  args: { id: string; name: string; description?: string }
): Promise<AdminProduct> {
  const res = await authedFetch(getToken, "/admin/suite/products", {
    method: "POST",
    body: JSON.stringify(args),
  });
  return parseOrThrow<AdminProduct>(res);
}

export async function adminDeleteProduct(getToken: GetToken, productId: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/products/${encodeURIComponent(productId)}`, {
    method: "DELETE",
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminGetManageTeamUrl(
  getToken: GetToken,
  productId: string,
  orgId: string,
): Promise<string> {
  const res = await authedFetch(
    getToken,
    `/admin/suite/manage-team-url?productId=${encodeURIComponent(productId)}&orgId=${encodeURIComponent(orgId)}`,
  );
  const body = await parseOrThrow<{ url: string }>(res);
  return body.url;
}

export async function adminListGroups(getToken: GetToken): Promise<SuiteGroup[]> {
  const res = await authedFetch(getToken, "/admin/suite/groups");
  return parseOrThrow<SuiteGroup[]>(res);
}

export async function adminCreateGroup(
  getToken: GetToken,
  args: { orgId: string; name: string; description?: string }
): Promise<SuiteGroup> {
  const res = await authedFetch(getToken, "/admin/suite/groups", {
    method: "POST",
    body: JSON.stringify(args),
  });
  return parseOrThrow<SuiteGroup>(res);
}

export async function adminListUserGroupAssignments(getToken: GetToken): Promise<UserGroupAssignment[]> {
  const res = await authedFetch(getToken, "/admin/suite/user-group-assignments");
  return parseOrThrow<UserGroupAssignment[]>(res);
}

export async function adminDeleteGroup(getToken: GetToken, groupId: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/groups/${encodeURIComponent(groupId)}`, { method: "DELETE" });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminGetGroupMembers(getToken: GetToken, groupId: string): Promise<GroupMember[]> {
  const res = await authedFetch(getToken, `/admin/suite/groups/${encodeURIComponent(groupId)}/members`);
  return parseOrThrow<GroupMember[]>(res);
}

export async function adminAddGroupMember(getToken: GetToken, groupId: string, clerkUserId: string): Promise<void> {
  const res = await authedFetch(getToken, `/admin/suite/groups/${encodeURIComponent(groupId)}/members`, {
    method: "POST",
    body: JSON.stringify({ clerkUserId }),
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminRemoveGroupMember(getToken: GetToken, groupId: string, userId: string): Promise<void> {
  const res = await authedFetch(
    getToken,
    `/admin/suite/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" }
  );
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminGetGroupToolRoles(getToken: GetToken, groupId: string): Promise<GroupToolRole[]> {
  const res = await authedFetch(getToken, `/admin/suite/groups/${encodeURIComponent(groupId)}/tool-roles`);
  return parseOrThrow<GroupToolRole[]>(res);
}

export async function adminSetGroupToolRole(
  getToken: GetToken,
  groupId: string,
  args: { productId: string; role: string }
): Promise<void> {
  const res = await authedFetch(
    getToken,
    `/admin/suite/groups/${encodeURIComponent(groupId)}/tool-roles`,
    { method: "PUT", body: JSON.stringify(args) }
  );
  await parseOrThrow<{ ok: true }>(res);
}

// ── Notification email settings ─────────────────────────────────────────────

export interface NotificationEmailSettings {
  emails: string[];
  envFallback: string | null;
}

export async function adminGetNotificationEmails(
  getToken: GetToken
): Promise<NotificationEmailSettings> {
  const res = await authedFetch(getToken, "/admin/suite/settings/notification-emails");
  return parseOrThrow<NotificationEmailSettings>(res);
}

export async function adminSetNotificationEmails(
  getToken: GetToken,
  emails: string[]
): Promise<void> {
  const res = await authedFetch(getToken, "/admin/suite/settings/notification-emails", {
    method: "PUT",
    body: JSON.stringify({ emails }),
  });
  await parseOrThrow<{ ok: true }>(res);
}

export async function adminSendTestNotificationEmail(
  getToken: GetToken
): Promise<{ sentTo: string[] }> {
  const res = await authedFetch(getToken, "/admin/suite/settings/notification-emails/test", {
    method: "POST",
  });
  return parseOrThrow<{ ok: true; sentTo: string[] }>(res);
}
